import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  integrationsApi,
  type Integration,
  type IntegrationField,
  type IntegrationMechanic,
} from '../../api/integrations.api';
import { settingsHistoryKey } from '../../api/settings-history.api';
import { APP_LOCALE } from '../../utils/locale.js';

import { SelectSettingField } from './CommissionTransferControls';
import {
  CurrencyMapField,
  type CurrencyMapValue,
} from './CurrencyMapField';
import { SettingsHistoryButton } from './SettingsHistory';
import { SettingsSaveBar } from './SettingsSaveBar';
import { SettingsSectionHeader } from './SettingsSectionHeader';
import { pendingChanges } from './settings-changes';
import { noRestoreNote, sectionPath } from './settings-sections';

const TREASURY_ACCOUNTS = sectionPath({ group: 'treasury', id: 'accounts' });

/**
 * System settings — where partner credentials are configured.
 *
 * Until this existed, changing a partner meant editing a committed env file and
 * waiting for a deploy, which is not a thing anyone can do during an incident.
 *
 * **One card per vendor, one switch per mechanic.** The same GuruPay instance
 * can issue a vIBAN per customer or hold everybody on its master account — same
 * API key, same pooled customer, same master. Asking for those credentials
 * twice would have been a fiction about their API, and would have meant two
 * webhook URLs where their console has one setting.
 *
 * Two properties this screen has to keep:
 *
 *  - A secret is write-only. It is never sent back, so the field shows only
 *    whether one is stored — which is why every credential says so out loud.
 *  - A mechanic cannot be switched on while the connection is incomplete. A
 *    half-configured partner accepts an account request and then cannot finish
 *    it, which is worse than one that is plainly off.
 */

const ACCOUNT_MODEL_LABELS: Record<string, string> = {
  INTERNAL: 'Our own ledger — IBAN issued here, immediately',
  DEDICATED_IBAN: 'One IBAN per customer, issued by the partner asynchronously',
  SHARED_ACCOUNT: 'One shared account — customers separated by payment reference',
};

/**
 * Whether a credential is stored, said out loud.
 *
 * A secret is write-only, so the field looks identical whether one is saved or
 * the box has never been filled in. That is the correct security property and a
 * terrible signal: the only way to find out used to be to save something and
 * see whether the error changed.
 */
function CredentialState({
  field,
  pending,
}: {
  field: IntegrationField;
  /** What the operator has typed but not yet saved. */
  pending: string | undefined;
}): JSX.Element | null {
  if (!field.secret) return null;

  if (pending !== undefined && pending !== '') {
    return <Chip size="small" color="info" variant="outlined" label="Unsaved" />;
  }
  if (field.isSet) {
    return (
      <Tooltip title="A value is stored. It is never shown again — leave this blank to keep it, or type a new one to replace it.">
        <Chip size="small" color="success" variant="outlined" label="Set" />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={
        field.required
          ? 'Nothing is stored. This connection cannot be used until it is.'
          : 'Nothing is stored.'
      }
    >
      <Chip
        size="small"
        color={field.required ? 'warning' : 'default'}
        variant="outlined"
        label="Not set"
      />
    </Tooltip>
  );
}

/** One job the connection can do, with its own switch. */
function MechanicRow({
  mechanic,
  busy,
  values,
  maps,
  onChange,
  onMapChange,
  onToggle,
  vendorId,
}: {
  mechanic: IntegrationMechanic;
  busy: boolean;
  values: Record<string, string>;
  maps: Record<string, CurrencyMapValue>;
  onChange: (key: string, value: string) => void;
  onMapChange: (key: string, next: CurrencyMapValue) => void;
  onToggle: (on: boolean) => void;
  vendorId: string;
}): JSX.Element {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ maxWidth: 720 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">{mechanic.variant}</Typography>
            <Tooltip title={ACCOUNT_MODEL_LABELS[mechanic.accountModel] ?? ''}>
              <Chip size="small" variant="outlined" label={mechanic.accountModel} />
            </Tooltip>
            <Tooltip title="The code recorded on every account opened here.">
              <Chip size="small" variant="outlined" label={mechanic.id} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {mechanic.description}
          </Typography>
          {mechanic.contractStatus !== undefined ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {mechanic.contractStatus}
            </Alert>
          ) : null}
        </Box>

        {mechanic.alwaysOn ? (
          <Chip size="small" color="success" variant="outlined" label="Always available" />
        ) : (
          <Tooltip
            title={mechanic.ready ? '' : `Still needs: ${mechanic.missingFields.join(', ')}`}
          >
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={mechanic.switchedOn}
                    disabled={!mechanic.ready || busy}
                    onChange={(event) => onToggle(event.target.checked)}
                  />
                }
                label={mechanic.switchedOn ? 'On' : 'Off'}
              />
            </span>
          </Tooltip>
        )}
      </Stack>

      {mechanic.fields.length > 0 ? (
        <Stack spacing={2} sx={{ mt: 2, maxWidth: 720 }}>
          {mechanic.fields.map((field) =>
            field.kind === 'currency-map' ? (
              <Box key={field.key}>
                <CurrencyMapField
                  field={field}
                  value={maps[field.key] ?? (field.value as CurrencyMapValue | null) ?? {}}
                  onChange={(next) => onMapChange(field.key, next)}
                />
              </Box>
            ) : (
              <TextField
                key={field.key}
                fullWidth
                size="small"
                label={`${field.label}${field.required ? '' : ' (optional)'}`}
                type={field.secret ? 'password' : 'text'}
                value={values[field.key] ?? (field.secret ? '' : String(field.value ?? ''))}
                onChange={(event) => onChange(field.key, event.target.value)}
                placeholder={
                  field.secret && field.isSet
                    ? 'Stored — leave blank to keep it'
                    : (field.placeholder ?? '')
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: <CredentialState field={field} pending={values[field.key]} />,
                }}
                helperText={field.help}
                autoComplete="new-password"
                inputProps={{
                  name: `nvx-${vendorId.toLowerCase()}-${mechanic.id.toLowerCase()}-${field.key}`,
                  autoComplete: 'new-password',
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                  spellCheck: false,
                }}
              />
            ),
          )}
        </Stack>
      ) : null}

      {!mechanic.alwaysOn && !mechanic.ready ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          Still needs: {mechanic.missingFields.join(', ')}
          {mechanic.missingFields.some((f) => f.includes('(Treasury')) ? (
            <>
              {' '}— accounts are set in{' '}
              <Link component={RouterLink} to={TREASURY_ACCOUNTS}>Treasury → Accounts</Link>.
            </>
          ) : null}
        </Typography>
      ) : null}
    </Box>
  );
}

function IntegrationCard({ integration }: { integration: Integration }): JSX.Element {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  // Currency maps are objects, so they are held apart from the plain string
  // fields rather than stringified into them and parsed back out.
  const [maps, setMaps] = useState<Record<string, CurrencyMapValue>>({});
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);

  // One URL per vendor, because a partner console has one webhook setting. The
  // same connection sends account issuance and transaction notifications.
  // IBANQ signs its deliveries (RFC 9421) and registers the URL by hand on
  // their side, so it gets its own receiver and no Register button (DEV-149).
  const ibanq = integration.vendorId === 'IBANQ';
  const webhookUrl = ibanq
    ? `${window.location.origin}/api/internal/webhooks/ibanq`
    : `${window.location.origin}/api/internal/webhooks/${integration.vendorId}`;
  const issuesAsynchronously = !ibanq && integration.mechanics.some(
    (m) => m.accountModel === 'DEDICATED_IBAN',
  );

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };

  // DEV-133: what this card is about to change, for the shared save bar. Any
  // credential needs a reason, which the API requires. The accounts are in
  // Settings → Treasury (DEV-139), so the card holds the connection only.
  const editableFields = [
    ...integration.fields,
    ...integration.mechanics.flatMap((m) => m.fields),
  ].filter((field, index, all) => all.findIndex((f) => f.key === field.key) === index);
  const changes = pendingChanges(editableFields, values, maps);
  const [savedAt, setSavedAt] = useState(0);

  const save = useMutation({
    onMutate: () => setError(''),
    mutationFn: (input: { mechanics?: Record<string, boolean>; reason?: string }) => {
      const config: Record<string, unknown> = {};
      const secrets: Record<string, string> = {};
      // The connection's fields and every mechanic's own fields are edited on
      // one card and saved together — they all live in the same vendor row.
      const allFields = [...integration.fields, ...integration.mechanics.flatMap((m) => m.fields)];
      for (const field of allFields) {
        if (field.kind === 'currency-map') {
          const edited = maps[field.key];
          if (edited !== undefined) config[field.key] = edited;
          continue;
        }
        const entered = values[field.key];
        if (entered === undefined) continue;
        if (field.secret) {
          // Untouched secrets are not sent at all, so they keep their value.
          if (entered !== '') secrets[field.key] = entered;
        } else {
          config[field.key] = entered;
        }
      }
      return integrationsApi.update(integration.vendorId, {
        config,
        secrets,
        ...(input.mechanics !== undefined ? { mechanics: input.mechanics } : {}),
        ...(input.reason !== undefined ? { reason: input.reason } : {}),
      });
    },
    onSuccess: async () => {
      setValues({});
      setMaps({});
      setError('');
      setSavedAt(Date.now());
      await refresh();
      await queryClient.invalidateQueries({ queryKey: settingsHistoryKey('INTEGRATION', integration.vendorId) });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not save those settings.');
    },
  });

  // An email or SMS provider is tested by sending a real message somewhere.
  const messaging = integration.kind === 'MESSAGING';
  const [testTo, setTestTo] = useState('');
  const test = useMutation({
    mutationFn: () => integrationsApi.test(integration.vendorId, messaging ? testTo.trim() : undefined),
    onSuccess: (result) => setTestResult(result),
    onError: (err: unknown) =>
      setTestResult({
        ok: false,
        detail: err instanceof Error ? err.message : 'The test could not be run.',
      }),
  });

  const registerWebhook = useMutation({
    mutationFn: () => integrationsApi.registerWebhook(integration.vendorId, webhookUrl),
    onSuccess: (result) => setTestResult(result),
    onError: (err: unknown) =>
      setTestResult({
        ok: false,
        detail: err instanceof Error ? err.message : 'The registration could not be run.',
      }),
  });

  const anyOn = integration.mechanics.some((m) => m.enabled);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">{integration.vendor}</Typography>
        {integration.nothingToConfigure ? (
          <Chip size="small" color="success" variant="outlined" label="Always available" />
        ) : anyOn ? (
          <Chip size="small" color="success" label="In use" />
        ) : (
          <Chip size="small" variant="outlined" label="Off" />
        )}
        <Box sx={{ flex: 1 }} />
        {integration.nothingToConfigure ? null : (
          <SettingsHistoryButton subject="INTEGRATION" subjectId={integration.vendorId} title={integration.vendor}
            restoreNote={noRestoreNote({ group: 'connections', id: 'providers' })} />
        )}
      </Stack>

      {!integration.nothingToConfigure && !integration.configured ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The connection is incomplete — still needed: {integration.missingFields.join(', ')}.
          Nothing can be switched on until it is.
        </Alert>
      ) : null}

      {/* With unsaved changes the save bar shows the error beside them. */}
      {error !== '' && changes.length === 0 ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {testResult !== null ? (
        <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
          {testResult.detail}
        </Alert>
      ) : null}

      {integration.nothingToConfigure ? null : (
        <>
          <Typography variant="overline" color="text.secondary">
            Connection
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* The accounts and the commission transfer are Settings -> Treasury
                (DEV-139): this card holds the connection and credentials only. */}
            {integration.fields.map((field) => (
              <Box key={field.key}>
                {field.kind === 'currency-map' ? (
                  <CurrencyMapField
                    field={field}
                    value={maps[field.key] ?? (field.value as CurrencyMapValue | null) ?? {}}
                    onChange={(next) => setMaps((current) => ({ ...current, [field.key]: next }))}
                  />
                ) : field.kind === 'select' ? (
                  <SelectSettingField
                    field={field}
                    vendorId={integration.vendorId}
                    value={values[field.key]}
                    onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={`${field.label}${field.required ? '' : ' (optional)'}`}
                    type={field.secret ? 'password' : 'text'}
                    value={values[field.key] ?? (field.secret ? '' : String(field.value ?? ''))}
                    onChange={(event) =>
                      setValues((current) => ({ ...current, [field.key]: event.target.value }))
                    }
                    placeholder={
                      field.secret && field.isSet
                        ? 'Stored — leave blank to keep it'
                        : (field.placeholder ?? '')
                    }
                    // Shrunk always, so the "Stored — leave blank to keep it"
                    // placeholder is readable: MUI hides a placeholder behind the
                    // label until the field is focused, which is exactly when
                    // nobody is looking for it.
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <CredentialState field={field} pending={values[field.key]} />,
                    }}
                    helperText={field.help}
                    // Chrome ignores autocomplete="off" on anything resembling a
                    // login and will put the operator's own email in "API base URL"
                    // and their saved password in "API key". `new-password` is the
                    // one value it honours, and a field name it has never seen
                    // keeps its heuristics away from the rest.
                    autoComplete="new-password"
                    inputProps={{
                      name: `nvx-${integration.vendorId.toLowerCase()}-${field.key}`,
                      autoComplete: 'new-password',
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      spellCheck: false,
                    }}
                  />
                )}
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Typography variant="overline" color="text.secondary">
        {integration.mechanics.length > 1 ? 'What this connection can do' : 'What this does'}
      </Typography>
      <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
        {integration.mechanics.map((mechanic) => (
          <MechanicRow
            key={mechanic.id}
            mechanic={mechanic}
            vendorId={integration.vendorId}
            busy={save.isPending}
            values={values}
            maps={maps}
            onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
            onMapChange={(key, next) => setMaps((current) => ({ ...current, [key]: next }))}
            onToggle={(on) => {
              // A switch saves the card's pending edits with it; one that needs a
              // reason must go through the save bar's review first (DEV-133).
              if (changes.some((c) => c.needsReason)) {
                setError('Save or discard the unsaved changes below first: they need a reason.');
                return;
              }
              save.mutate({ mechanics: { [mechanic.id]: on } });
            }}
          />
        ))}
      </Stack>

      {integration.nothingToConfigure ? null : (
        <>
          <Divider sx={{ my: 2 }} />
          {/* The save bar, at the end — it covers everything on the card,
              including each mechanic's own fields, so putting it above them
              made it look like it did not (DEV-133: shared bar, review, reason). */}
          <SettingsSaveBar
            changes={changes}
            saving={save.isPending}
            error={error}
            savedAt={savedAt}
            onDiscard={() => {
              setValues({});
              setMaps({});
              setError('');
            }}
            onSave={(reason) => save.mutate(reason === undefined ? {} : { reason })}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 3 }} alignItems="center" flexWrap="wrap">
            {messaging ? (
              <TextField
                size="small"
                label={integration.vendorId === 'TWILIO' ? 'Send a test SMS to' : 'Send a test email to'}
                placeholder={integration.vendorId === 'TWILIO' ? '+37060000000' : 'you@norvix.com'}
                value={testTo}
                onChange={(event) => setTestTo(event.target.value)}
                sx={{ minWidth: 260 }}
              />
            ) : null}
            <Button
              variant="outlined"
              onClick={() => test.mutate()}
              disabled={test.isPending || !integration.configured || (messaging && testTo.trim() === '')}
            >
              {test.isPending ? (messaging ? 'Sending…' : 'Testing…') : messaging ? 'Send test' : 'Test connection'}
            </Button>
            {issuesAsynchronously ? (
              <Tooltip
                title={`Tells ${integration.vendor} to call ${webhookUrl}. Until they are told, an issued IBAN never reaches us and every account waits.`}
              >
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => registerWebhook.mutate()}
                    disabled={registerWebhook.isPending || !anyOn}
                  >
                    {registerWebhook.isPending ? 'Registering…' : 'Register webhook'}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Stack>

          {integration.kind === 'PAYMENT' ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, fontFamily: 'monospace' }}
            >
              Webhook URL: {webhookUrl}
            </Typography>
          ) : null}
          {ibanq ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Give this URL to IBANQ: they register it by hand, for inbound, outbound, internal, FX and sub-account events.
              Deliveries are verified against IBANQ’s published keys and refused unless signed; an events poll runs every
              minute as well, so nothing is lost while the webhook is not registered.
            </Typography>
          ) : null}
        </>
      )}

      {integration.nothingToConfigure ? null : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {integration.updatedAt === null
            ? 'Never configured.'
            : `Last changed ${new Date(integration.updatedAt).toLocaleString(APP_LOCALE)} by ${integration.updatedBy ?? 'unknown'}.`}
        </Typography>
      )}
    </Paper>
  );
}

export function SystemSettingsPage(): JSX.Element {
  const integrationsQuery = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.list(),
  });

  if (integrationsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (integrationsQuery.isError) {
    return <Alert severity="error">Could not load the integration settings.</Alert>;
  }

  const integrations = integrationsQuery.data ?? [];
  const payment = integrations.filter((i) => i.kind === 'PAYMENT');
  const identity = integrations.filter((i) => i.kind === 'IDENTITY');
  const messagingProviders = integrations.filter((i) => i.kind === 'MESSAGING');

  return (
    <Box>
      <SettingsSectionHeader title="Providers">
        Every partner this platform talks to, in one place. Credentials are entered once per
        partner; what that connection is allowed to do is switched on separately. A payment partner
        appears as a choice when opening an account only once its connection is complete and the
        mechanic is on. Secrets are stored encrypted and are never shown again — leave a secret
        field blank to keep the one already stored. Where Norvix commissions go and when they are
        moved is set under Treasury → Commission transfer. Changing a master account or a
        credential asks for a reason, kept with the change in each provider&apos;s History.
      </SettingsSectionHeader>

      <Typography variant="overline" color="text.secondary">
        Payment providers — where customer money is held
      </Typography>
      <Box sx={{ mt: 1, mb: 4 }}>
        {payment.map((integration) => (
          <IntegrationCard key={integration.vendorId} integration={integration} />
        ))}
      </Box>

      <Typography variant="overline" color="text.secondary">
        Identity providers — who the customer is
      </Typography>
      <Box sx={{ mt: 1 }}>
        {identity.map((integration) => (
          <IntegrationCard key={integration.vendorId} integration={integration} />
        ))}
      </Box>

      {/* Email and SMS: the credentials, a switch, and a real test message.
          The API always served these; the page only rendered payment and
          identity providers, so they could not be set anywhere. */}
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
        Email and SMS — how customers and staff are reached
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 820 }}>
        Enter the credentials, send yourself a test, then switch the provider on. Sign-in codes and
        other messages go out through a provider only while it is on.
      </Typography>
      <Box sx={{ mt: 1 }}>
        {messagingProviders.map((integration) => (
          <IntegrationCard key={integration.vendorId} integration={integration} />
        ))}
      </Box>
    </Box>
  );
}
